import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Traveler, BRAND } from "../../types";
import { ImageUploader } from "@/components/ImageUploader";
import {
  validateName,
  validatePhone,
  validateIdCard,
  validateLicenseNo,
  validateLicenseType,
  validateLicenseExpiry,
} from "@/utils/validation";
import { toastWarning } from "@/utils/toast";

interface TravelerEditModalProps {
  traveler: Traveler;
  /** 订单类型："car" 显示全部字段（含驾照），"hotel" 只显示姓名+类型+身份证号 */
  orderType?: "car" | "hotel";
  /** 是否为新增模式（影响标题显示） */
  isAdd?: boolean;
  /** 保存中状态，由父组件控制 */
  saving?: boolean;
  onSave: (t: Traveler) => void | Promise<void>;
  onClose: () => void;
}

export function TravelerEditModal({
  traveler,
  orderType = "car",
  isAdd = false,
  saving = false,
  onSave,
  onClose,
}: TravelerEditModalProps) {
  const isCarOrder = orderType === "car";
  const [form, setForm] = useState<Traveler>({ ...traveler });

  const update = (k: keyof Traveler, v: string | boolean) =>
    setForm((p) => ({ ...p, [k]: v }));

  // 弹窗打开时锁定背景滚动
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center"
      style={{ background: "rgba(0,0,0,0.5)" }}
    >
      <div
        className="bg-white rounded-t-3xl w-full flex flex-col"
        style={{
          maxWidth: 480,
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div className="w-10 h-1 rounded-full bg-gray-200 mx-auto mt-3 shrink-0" />
        {/* 头部 - 固定不滚动 */}
        <div
          className="flex items-center justify-between px-5 pt-4 pb-3 shrink-0"
          style={{ borderBottom: "1px solid #F5F5F5" }}
        >
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer"
            style={{ background: "#F5F5F5" }}
          >
            <i
              className="ri-close-line"
              style={{ color: "#374151", fontSize: 18 }}
            />
          </button>
          <span className="text-base font-bold text-gray-900">
            {isAdd ? "添加出行人" : "编辑出行人员"}
          </span>
          <button
            onClick={() => {
              // 基础信息校验：姓名必填
              const nameCheck = validateName(form.name);
              if (!nameCheck.valid) {
                toastWarning(nameCheck.message);
                return;
              }

              // 联系电话（仅租车订单需要）
              if (isCarOrder) {
                const phoneCheck = validatePhone(form.phone || "");
                if (!phoneCheck.valid) {
                  toastWarning(phoneCheck.message);
                  return;
                }
              }

              // 身份证号码校验（通用）
              const idCardCheck = validateIdCard(form.idCard || "");
              if (!idCardCheck.valid) {
                toastWarning(idCardCheck.message);
                return;
              }

              // 身份证照片上传（仅租车订单需要）
              if (isCarOrder) {
                if (!form.idCardFront) {
                  toastWarning("请上传身份证正面照片");
                  return;
                }
                if (!form.idCardBack) {
                  toastWarning("请上传身份证反面照片");
                  return;
                }
              }

              // 驾照信息校验（仅租车订单且为主驾时）
              if (form.isDriver) {
                const licNo = validateLicenseNo(form.licenseNo || "");
                if (!licNo.valid) {
                  toastWarning(licNo.message);
                  return;
                }
                const licType = validateLicenseType(form.licenseType || "");
                if (!licType.valid) {
                  toastWarning(licType.message);
                  return;
                }
                const licExp = validateLicenseExpiry(form.licenseExpiry || "");
                if (!licExp.valid) {
                  toastWarning(licExp.message);
                  return;
                }
                if (!form.licenseFront) {
                  toastWarning("请上传驾照正页照片");
                  return;
                }
                if (!form.licenseBack) {
                  toastWarning("请上传驾照副页照片");
                  return;
                }
              }

              onSave(form);
              onClose();
            }}
            className="text-sm font-bold cursor-pointer whitespace-nowrap px-4 py-1.5 rounded-full text-white flex items-center gap-1.5"
            style={{ background: saving ? "#CCC" : BRAND }}
            disabled={saving}
          >
            {saving && (
              <i
                className="ri-loader-4-line animate-spin"
                style={{ fontSize: 14 }}
              />
            )}
            {saving ? "保存中..." : "保存"}
          </button>
        </div>

        <div
          className="px-5 py-4  space-y-4 pb-8 overflow-y-auto"
          style={{ flex: "1 1 0%", minHeight: 0 }}
        >
          {/* 基本信息 */}
          <div>
            <div className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
              <i
                className="ri-user-line"
                style={{ color: BRAND, fontSize: 15 }}
              />
              基本信息
            </div>
            <div className="space-y-2.5">
              {/* 真实姓名（通用） */}
              {[
                {
                  key: "name" as keyof Traveler,
                  label: "真实姓名",
                  ph: "请输入与证件一致的姓名",
                  icon: "ri-user-line",
                },
                // 联系电话（仅租车订单）
                ...(isCarOrder
                  ? [{
                      key: "phone" as keyof Traveler,
                      label: "联系电话",
                      ph: "请输入手机号码",
                      icon: "ri-phone-line",
                    }]
                  : []),
              ].map((f) => (
                <div key={f.key}>
                  <div className="text-xs text-gray-500 mb-1">{f.label}</div>
                  <div
                    className="flex items-center gap-2.5 px-3.5 py-3 rounded-xl"
                    style={{
                      background: "#F8F9FC",
                      border: "1px solid #F0F0F0",
                    }}
                  >
                    <i
                      className={f.icon}
                      style={{ color: "#C0C4CC", fontSize: 15 }}
                    />
                    <input
                      className="flex-1 bg-transparent outline-none text-sm text-gray-700"
                      placeholder={f.ph}
                      value={(form[f.key] as string) ?? ""}
                      onChange={(e) => update(f.key, e.target.value)}
                    />
                  </div>
                </div>
              ))}
              {/* 人员类型 */}
              <div>
                <div className="text-xs text-gray-500 mb-1">人员类型</div>
                <div className="flex gap-2">
                  {["成人", "儿童 (6-12岁)", "幼儿 (<6岁)"].map((t) => (
                    <button
                      key={t}
                      onClick={() => update("type", t)}
                      className="flex-1 py-2 rounded-xl text-xs font-medium cursor-pointer transition-all whitespace-nowrap"
                      style={{
                        background: form.type === t ? "#FFF3EE" : "#F5F5F5",
                        color: form.type === t ? BRAND : "#6B7280",
                        border: `1.5px solid ${form.type === t ? BRAND : "transparent"}`,
                      }}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* 证件信息 */}
          <div>
            <div className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
              <i
                className="ri-id-card-line"
                style={{ color: BRAND, fontSize: 15 }}
              />
              身份证信息
            </div>
            <div className="space-y-2.5">
              <div>
                <div className="text-xs text-gray-500 mb-1">身份证号码</div>
                <div
                  className="flex items-center gap-2.5 px-3.5 py-3 rounded-xl"
                  style={{ background: "#F8F9FC", border: "1px solid #F0F0F0" }}
                >
                  <i
                    className="ri-bank-card-line"
                    style={{ color: "#C0C4CC", fontSize: 15 }}
                  />
                  <input
                    className="flex-1 bg-transparent outline-none text-sm text-gray-700"
                    placeholder="请输入18位身份证号码"
                    value={form.idCard ?? ""}
                    onChange={(e) => update("idCard", e.target.value)}
                  />
                </div>
              </div>
              {/* 身份证上传 - 正反面（仅租车订单需要） */}
              {isCarOrder && (
              <div>
                <div className="text-xs text-gray-500 mb-2">上传身份证照片</div>
                <div className="grid grid-cols-2 gap-3">
                  <ImageUploader
                    remote
                    value={form.idCardFront}
                    onChange={(url) => update("idCardFront", url ?? "")}
                    placeholder="点击上传\n身份证正面"
                    height={90}
                    brandColor={BRAND}
                  />
                  <ImageUploader
                    remote
                    value={form.idCardBack}
                    onChange={(url) => update("idCardBack", url ?? "")}
                    placeholder="点击上传\n身份证反面"
                    height={90}
                    brandColor={BRAND}
                  />
                </div>
                {form.idCardFront && form.idCardBack && (
                  <div className="flex items-center gap-1.5 mt-2">
                    <i
                      className="ri-checkbox-circle-fill"
                      style={{ color: "#10B981", fontSize: 13 }}
                    />
                    <span className="text-xs" style={{ color: "#10B981" }}>
                      身份证正反面已上传
                    </span>
                  </div>
                )}
              </div>
              )} {/* end isCarOrder: 身份证上传 */}
            </div>
          </div>

          {/* 驾照信息（仅租车订单且选为主驾） */}
          {isCarOrder && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-bold text-gray-900 flex items-center gap-2">
                  <i
                    className="ri-car-line"
                    style={{ color: BRAND, fontSize: 15 }}
                  />
                  驾照信息
                  <span className="text-xs font-normal text-gray-400">
                    （租车人必填）
                  </span>
                </div>
                <button
                  onClick={() => update("isDriver", !form.isDriver)}
                  className="flex items-center gap-1.5 cursor-pointer"
                >
                  <div
                    className="w-10 h-5 rounded-full transition-all flex items-center px-0.5"
                    style={{ background: form.isDriver ? BRAND : "#D1D5DB" }}
                  >
                    <div
                      className="w-4 h-4 rounded-full bg-white transition-all"
                      style={{
                        transform: form.isDriver
                          ? "translateX(20px)"
                          : "translateX(0)",
                      }}
                    />
                  </div>
                  <span className="text-xs text-gray-500">
                    {form.isDriver ? "是主驾" : "设为主驾"}
                  </span>
                </button>
              </div>

              {form.isDriver && (
                <div className="space-y-2.5">
                  {[
                    {
                      key: "licenseNo" as keyof Traveler,
                      label: "驾照证号",
                      ph: "请输入驾照号码",
                      icon: "ri-car-line",
                    },
                    {
                      key: "licenseType" as keyof Traveler,
                      label: "驾照类型",
                      ph: "如：C1、B2",
                      icon: "ri-road-map-line",
                    },
                    {
                      key: "licenseExpiry" as keyof Traveler,
                      label: "有效期至",
                      ph: "如：2030-01-01",
                      icon: "ri-calendar-line",
                    },
                  ].map((f) => (
                    <div key={f.key}>
                      <div className="text-xs text-gray-500 mb-1">
                        {f.label}
                      </div>
                      <div
                        className="flex items-center gap-2.5 px-3.5 py-3 rounded-xl"
                        style={{
                          background: "#F8F9FC",
                          border: "1px solid #F0F0F0",
                        }}
                      >
                        <i
                          className={f.icon}
                          style={{ color: "#C0C4CC", fontSize: 15 }}
                        />
                        <input
                          className="flex-1 bg-transparent outline-none text-sm text-gray-700"
                          placeholder={f.ph}
                          value={(form[f.key] as string) ?? ""}
                          onChange={(e) => update(f.key, e.target.value)}
                        />
                      </div>
                    </div>
                  ))}

                  {/* 驾照上传 - 正页副页 */}
                  <div>
                    <div className="text-xs text-gray-500 mb-2">
                      上传驾照照片
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <ImageUploader
                        remote
                        value={form.licenseFront}
                        onChange={(url) => update("licenseFront", url ?? "")}
                        placeholder="点击上传\n驾照正页"
                        height={90}
                        brandColor={BRAND}
                      />
                      <ImageUploader
                        remote
                        value={form.licenseBack}
                        onChange={(url) => update("licenseBack", url ?? "")}
                        placeholder="点击上传\n驾照副页"
                        height={90}
                        brandColor={BRAND}
                      />
                    </div>
                    {form.licenseFront && form.licenseBack && (
                      <div className="flex items-center gap-1.5 mt-2">
                        <i
                          className="ri-checkbox-circle-fill"
                          style={{ color: "#10B981", fontSize: 13 }}
                        />
                        <span className="text-xs" style={{ color: "#10B981" }}>
                          驾照正副页已上传
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 设为默认出行人 */}
          <button
            type="button"
            onClick={() => update("isDefault", !form.isDefault)}
            className="w-full flex items-center gap-2.5 py-3 cursor-pointer"
          >
            <div
              className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
              style={{
                border: form.isDefault ? `1.8px solid ${BRAND}` : "1.5px solid #D1D5DB",
                background: form.isDefault ? BRAND : "transparent",
              }}
            >
              {form.isDefault && (
                <i
                  className="ri-check-line text-white"
                  style={{ fontSize: 11 }}
                />
              )}
            </div>
            <span className="text-sm text-gray-700">
              设为默认出行人
            </span>
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
